import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import type { ComponentProps } from 'react'
import { cn } from './cn.js'

const button = cva(
  // 600, not 500: the type contract ships exactly two weights (400/600), so a
  // font-medium here would render a synthesized in-between.
  'inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-control)] font-semibold ' +
    'transition-[background-color,border-color,color,transform] duration-[140ms] ' +
    'ease-[var(--ease-out-quick)] active:scale-[0.98] ' +
    'disabled:pointer-events-none disabled:opacity-40 select-none whitespace-nowrap',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-ink hover:brightness-105',
        secondary: 'bg-raised text-ink border border-line hover:border-line-strong',
        ghost: 'text-dim hover:bg-raised hover:text-ink',
        danger: 'bg-transparent text-danger border border-line hover:border-danger',
      },
      size: {
        // 44px is the smallest reliable thumb target on a phone.
        md: 'h-11 px-4 text-base',
        sm: 'h-8 px-2.5 text-sm',
        lg: 'h-12 px-5 text-lg',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'secondary', size: 'md', block: false },
  },
)

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof button> {
  asChild?: boolean
}

export function Button({
  className,
  variant,
  size,
  block,
  asChild = false,
  type = 'button',
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button'
  return (
    <Comp
      type={asChild ? undefined : type}
      className={cn(button({ variant, size, block }), className)}
      {...props}
    />
  )
}

export interface IconButtonProps extends ComponentProps<'button'> {
  label: string
  size?: 'sm' | 'md'
}

/** Square tap target for toolbar actions; the label is for screen readers. */
export function IconButton({
  className,
  label,
  size = 'md',
  type = 'button',
  ...props
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      className={cn(
        'inline-flex items-center justify-center rounded-[var(--radius-control)] text-dim',
        'transition-colors duration-[140ms] ease-[var(--ease-out-quick)]',
        'hover:bg-raised hover:text-ink active:scale-[0.96] disabled:opacity-40',
        size === 'md' ? 'size-11' : 'size-8',
        className,
      )}
      {...props}
    />
  )
}
